import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronDownIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { InvoiceItem } from '../types/Invoice';
export function InvoiceForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('id');
  const isEditMode = !!invoiceId;
  const [formData, setFormData] = useState({
    invoiceNumber: isEditMode ? 'INV-2024-001' : '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    notes: '',
    status: 'draft' as 'draft' | 'sent'
  });
  const [items, setItems] = useState<InvoiceItem[]>([{
    id: '1',
    description: '',
    quantity: 1,
    unitPrice: 0,
    total: 0
  }]);
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const mockCustomers = [{
    id: '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+66 12 345 6789'
  }, {
    id: '2',
    name: 'Tech Solutions Ltd',
    email: 'accounts@techsolutions.th',
    phone: '+66 11 234 5678'
  }, {
    id: '3',
    name: 'Global Enterprises',
    email: 'finance@global.com',
    phone: '+66 13 456 7890'
  }, {
    id: '4',
    name: 'Innovation Hub',
    email: 'billing@innovationhub.th',
    phone: '+66 10 567 8901'
  }];
  const filteredCustomers = mockCustomers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase()));
  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Send to Customer'
  }];
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const calculateItemTotal = (quantity: number, unitPrice: number) => quantity * unitPrice;
  const calculateSubtotal = () => items.reduce((sum, item) => sum + item.total, 0);
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = calculateItemTotal(updatedItem.quantity, updatedItem.unitPrice);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const selectCustomer = (customer: (typeof mockCustomers)[0]) => {
    setFormData({
      ...formData,
      customerName: customer.name,
      customerEmail: customer.email,
      customerPhone: customer.phone
    });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving invoice:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const handleSaveAsDraft = () => {
    console.log('Saving as draft:', {
      formData,
      items
    });
    navigate('/invoices');
  };
  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white';
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-8979">
      {/* Header */}
      <div className="flex items-center" data-id="element-8980">
        <button onClick={() => navigate('/invoices')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-8981">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-8982" />
          {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8983">
        {/* Invoice Details Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-8984">
          <div className="p-6 border-b border-gray-100" data-id="element-8985">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-8986">
              Invoice Details
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-8987">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-8988">
              <div data-id="element-8989">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-8990">
                  Invoice Number
                </label>
                <input type="text" value={formData.invoiceNumber} onChange={e => setFormData({
                ...formData,
                invoiceNumber: e.target.value
              })} placeholder="Auto-generated if left empty" className={inputClass} data-id="element-8991" />
              </div>
              <div ref={statusDropdownRef} className="relative" data-id="element-8992">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-8993">
                  Status
                </label>
                <button type="button" onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white text-left" data-id="element-8994">
                  <span className="text-gray-900" data-id="element-8995">
                    {statusOptions.find(s => s.value === formData.status)?.label}
                  </span>
                  <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} data-id="element-8996" />
                </button>
                {showStatusDropdown && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1" data-id="element-8997">
                    {statusOptions.map(option => <button key={option.value} type="button" onClick={() => {
                  setFormData({
                    ...formData,
                    status: option.value as 'draft' | 'sent'
                  });
                  setShowStatusDropdown(false);
                }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8998">
                        <span data-id="element-8999">{option.label}</span>
                        {formData.status === option.value && <CheckIcon size={16} className="text-[#F7941D]" data-id="element-9000" />}
                      </button>)}
                  </div>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-9001">
              <div data-id="element-9002">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9003">
                  Issue Date <span className="text-red-500" data-id="element-9004">*</span>
                </label>
                <input type="date" required value={formData.issueDate} onChange={e => setFormData({
                ...formData,
                issueDate: e.target.value
              })} className={inputClass} data-id="element-9005" />
              </div>
              <div data-id="element-9006">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9007">
                  Due Date <span className="text-red-500" data-id="element-9008">*</span>
                </label>
                <input type="date" required value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className={inputClass} data-id="element-9009" />
              </div>
            </div>

            <div data-id="element-9010">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9011">
                Description
              </label>
              <input type="text" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Brief description of the invoice" className={inputClass} data-id="element-9012" />
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-9013">
          <div className="p-6 border-b border-gray-100" data-id="element-9014">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-9015">
              Customer Information
            </h3>
          </div>
          <div className="p-6 space-y-5" data-id="element-9016">
            {/* Searchable Customer Select */}
            <div ref={customerDropdownRef} className="relative" data-id="element-9017">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9018">
                Customer Name <span className="text-red-500" data-id="element-9019">*</span>
              </label>
              <div className="relative" data-id="element-9020">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-9021" />
                <input type="text" required value={showCustomerDropdown ? customerSearch : formData.customerName || customerSearch} onChange={e => {
                setCustomerSearch(e.target.value);
                setFormData({
                  ...formData,
                  customerName: '',
                  customerEmail: '',
                  customerPhone: ''
                });
                setShowCustomerDropdown(true);
              }} onFocus={() => setShowCustomerDropdown(true)} placeholder="Search or enter customer name..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-9022" />
              </div>
              {showCustomerDropdown && filteredCustomers.length > 0 && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto" data-id="element-9023">
                  {filteredCustomers.map(customer => <button key={customer.id} type="button" onClick={() => selectCustomer(customer)} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors" data-id="element-9024">
                      <div data-id="element-9025">
                        <div className="text-sm font-medium text-gray-900" data-id="element-9026">
                          {customer.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-9027">
                          {customer.email}
                        </div>
                      </div>
                      {formData.customerName === customer.name && <CheckIcon size={16} className="text-[#F7941D] flex-shrink-0" data-id="element-9028" />}
                    </button>)}
                </div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-9029">
              <div data-id="element-9030">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9031">
                  Email <span className="text-red-500" data-id="element-9032">*</span>
                </label>
                <input type="email" required value={formData.customerEmail} onChange={e => setFormData({
                ...formData,
                customerEmail: e.target.value
              })} placeholder="customer@example.com" className={inputClass} data-id="element-9033" />
              </div>
              <div data-id="element-9034">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9035">
                  Phone
                </label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({
                ...formData,
                customerPhone: e.target.value
              })} placeholder="+66 XX XXX XXXX" className={inputClass} data-id="element-9036" />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-9037">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-9038">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-9039">
              Invoice Items
            </h3>
            <button type="button" onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#F7941D] hover:bg-orange-50 rounded-lg transition-colors" data-id="element-9040">
              <PlusIcon size={16} data-id="element-9041" />
              Add item
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-9042">
            {items.map((item, index) => <div key={item.id} className="grid grid-cols-12 gap-3 items-start" data-id="element-9043">
                <div className="col-span-12 md:col-span-5" data-id="element-9044">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9045">
                      Description
                    </label>}
                  <input type="text" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Item description" className={inputClass} data-id="element-9046" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-9047">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9048">
                      Qty
                    </label>}
                  <input type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value))} className={inputClass} data-id="element-9049" />
                </div>
                <div className="col-span-4 md:col-span-2" data-id="element-9050">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9051">
                      Unit Price
                    </label>}
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value))} className={inputClass} data-id="element-9052" />
                </div>
                <div className="col-span-3 md:col-span-2" data-id="element-9053">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-9054">
                      Total
                    </label>}
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 font-medium" data-id="element-9055">
                    THB {item.total.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="col-span-1 flex items-end" data-id="element-9056">
                  {index === 0 && <div className="mb-1.5 text-sm" data-id="element-9057">&nbsp;</div>}
                  <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-9058">
                    <TrashIcon size={18} data-id="element-9059" />
                  </button>
                </div>
              </div>)}

            {/* Totals */}
            <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-9060">
              <div className="flex justify-end" data-id="element-9061">
                <div className="w-full max-w-xs space-y-2" data-id="element-9062">
                  <div className="flex justify-between py-1.5" data-id="element-9063">
                    <span className="text-sm text-gray-500" data-id="element-9064">Subtotal</span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-9065">
                      THB {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="flex justify-between py-2.5 border-t-2 border-gray-200" data-id="element-9066">
                    <span className="text-base font-semibold text-gray-900" data-id="element-9067">
                      Total
                    </span>
                    <span className="text-base font-bold text-gray-900" data-id="element-9068">
                      THB {calculateSubtotal().toLocaleString('en-US')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-9069">
          <div className="p-6 border-b border-gray-100" data-id="element-9070">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-9071">
              Notes
            </h3>
          </div>
          <div className="p-6" data-id="element-9072">
            <textarea value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={3} placeholder="Additional notes or payment terms..." className={`${inputClass} resize-none`} data-id="element-9073" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pb-8" data-id="element-9074">
          <button type="button" onClick={() => navigate('/invoices')} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-9075">
            Cancel
          </button>
          <button type="button" onClick={handleSaveAsDraft} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-9076">
            Save as Draft
          </button>
          <button type="submit" className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-9077">
            {isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>;
}